export type TextItemLite = { str: string };

export type RangeMode = {
  pageNumber: number;
  startItem: number;
  endItem: number;
};

const WORD_SEP = '[\\s"\'“”„‟‘’‚‛«»‹›`]+';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Zoekt `target` als aaneengesloten, woordscheiding-tolerante reeks in de tekstitems van één pagina. */
export function findRange(
  items: TextItemLite[],
  target: string,
): Omit<RangeMode, 'pageNumber'> | null {
  const words = target.toLowerCase().split(new RegExp(WORD_SEP)).filter(Boolean);
  if (words.length === 0) return null;

  let joined = '';
  const itemAt: number[] = [];
  for (let i = 0; i < items.length; i++) {
    for (const ch of items[i]?.str ?? '') {
      joined += ch.toLowerCase();
      itemAt.push(i);
    }
    joined += ' ';
    itemAt.push(-1);
  }

  const pattern = new RegExp(words.map(escapeRegex).join(WORD_SEP));
  const m = joined.match(pattern);
  if (!m || m.index === undefined) return null;

  const start = m.index;
  const end = start + m[0].length;

  let startItem = -1;
  let endItem = -1;
  for (let i = start; i < end; i++) {
    const at = itemAt[i] ?? -1;
    if (at >= 0) {
      startItem = at;
      break;
    }
  }
  for (let i = end - 1; i >= start; i--) {
    const at = itemAt[i] ?? -1;
    if (at >= 0) {
      endItem = at;
      break;
    }
  }
  if (startItem === -1 || endItem === -1) return null;

  return { startItem, endItem };
}

/**
 * Probeert kandidaat-teksten in volgorde (backend-platte-tekst, dan de
 * client-side gestripte sentence als terugval) tegen eerst de voorkeurspagina
 * en daarna de overige pagina's, en geeft de eerste match terug.
 */
export function resolveHighlightMode(
  pageTexts: Map<number, TextItemLite[]>,
  candidates: string[],
  targetPage?: number,
): RangeMode | null {
  for (const candidate of candidates) {
    if (!candidate.trim()) continue;

    if (targetPage !== undefined) {
      const items = pageTexts.get(targetPage);
      if (items) {
        const range = findRange(items, candidate);
        if (range) return { pageNumber: targetPage, ...range };
      }
    }

    for (const [pageNumber, items] of pageTexts) {
      if (pageNumber === targetPage) continue;
      const range = findRange(items, candidate);
      if (range) return { pageNumber, ...range };
    }
  }

  return null;
}
